/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import UserProfile from '../routes/user-profile';
import UniversalTrackerManagement from '../routes/universal-tracker-management';
import UniversalTrackerProvenance from '../routes/universal-tracker-provenance';
import PublicSDGContributionChart from '../routes/public-sdg-contribution-chart';
import { RouteInterface, RoutesInterface } from '../types/routes';
import { portfolioTrackerRoutes } from '../routes/portfolio-tracker';
import { companyTrackerRoutes } from '../routes/company-tracker';
import { statsRoutes } from '../routes/stats';
import { assuranceRoutes } from '../routes/assurance';
import { sdgGapRoute } from '../routes/sdgap/SdgapRoute';
import { productsRoute } from '../routes/product-page-container';
import { worldTrackerRoute } from '../routes/world-tracker/WorldTrackerRoute';
import { matchPath } from 'react-router';
import { UserRoles } from './user';
import { materialityAssessmentRoutes } from '../apps/materiality-assessment/routes/MaterialityAssessmentRoute';
import { companyTrackerOnboardingRoute, esgenomeOnboardingRoute } from '../routes/company-tracker-light-onboarding';
import { SharedDashboard } from '../routes/custom-dashboard/shared-dashboard/SharedDashboard';
import { emissionsCalculatorsRoutes } from '../routes/emissions-calculators';

// NOTE: The order of this is important. It is the order in which routes are decided to render,
//       so put specific matches on top of broad ones!
const NonAppRoutes: RoutesInterface = {
  USER_PROFILE: {
    id: 'user_profile',
    label: 'User Profile',
    path: '/user-profile/:id?',
    component: UserProfile,
  },
  UTR_MANAGEMENT: {
    id: 'utr_management',
    label: 'My UTrs',
    path: '/universal-tracker-management/:initiativeId?',
    icon: 'fa-universal-access',
    hidden: true,
    auth: true,
    component: UniversalTrackerManagement,
    requiresInitiativeId: true,
  },
  UTRV_PROVENANCE: {
    id: 'utrv_provenance',
    label: 'Universal Tracker Provenance',
    path: '/universal-tracker-provenance/:utrId/:utrvId',
    icon: 'fa-universal-access',
    hidden: false,
    auth: true,
    component: UniversalTrackerProvenance,
  },
  PUBLIC_SDG_CHART: {
    id: 'public_sdg_chart',
    label: 'SDG Contribution Chart',
    path: '/public/:initiativeId/sdg-contribution-chart/:token',
    hidden: true,
    auth: false,
    component: PublicSDGContributionChart,
  },
  SHARED_CUSTOM_DASHBOARD: {
    id: 'shared_custom_dashboard',
    label: 'Shared Custom Dashboard',
    path: '/custom-dashboard/:dashboardId/token/:token',
    hidden: true,
    auth: false,
    component: SharedDashboard,
  },
  SGX_ESGENOME: {
    id: 'sgx_esgenome',
    label: 'SGX ESGenome',
    path: '/sgxesgenome',
  },
};

export const nonAppComponentRoutes: RouteInterface[] = Object.values(NonAppRoutes).filter(r => r.component);

export const ROUTES: RoutesInterface = {
  WORLD_TRACKER: worldTrackerRoute,
  ...companyTrackerRoutes,
  ...portfolioTrackerRoutes,
  ...assuranceRoutes,
  SDGAP: sdgGapRoute,
  ...emissionsCalculatorsRoutes,
  ...statsRoutes,
  ...materialityAssessmentRoutes,
  COMPANY_TRACKER_ONBOARDING: companyTrackerOnboardingRoute,
  ESGENOME_ONBOARDING: esgenomeOnboardingRoute,
  PRODUCTS: productsRoute,
  ...NonAppRoutes,
};

export const initiativeRoutes = [
  ROUTES.COMPANY_TRACKER_LIST,
  ROUTES.SUMMARY,
  ROUTES.DOWNLOADS,
  ROUTES.MANAGEMENT_FRAMEWORK,
  ROUTES.CUSTOM_METRICS,
];

export const getRouteTitle = (path: string) => {

  const defaultTitle = 'G17Eco Sustainability Reporting';

  if (!path || path === '/') {
    return defaultTitle;
  }

  const combinedRoutes = [
    ...initiativeRoutes,
    ...Object.values(ROUTES).reverse(),
  ];

  const currentRoute = combinedRoutes.find(r => {
    const match = matchPath(path, {
      path: r.path,
      exact: !!r.exact
    });
    return Boolean(match);
  });

  return currentRoute ? currentRoute.label : defaultTitle;
};

const userHasPermission = (requiredRoles?: UserRoles[], userPermissionsArray?: UserRoles[]) => {
  if (!requiredRoles) {
    if (userPermissionsArray && userPermissionsArray.length === 1 && userPermissionsArray[0] === UserRoles.Guest) {
      // If they are exclusively a guest, then I think we want to default to no-access?
      return false;
    }
    return true;
  }

  if (!userPermissionsArray) {
    return false;
  }

  return requiredRoles.some(requiredRole => userPermissionsArray.includes(requiredRole));
}

export const isRouteEnabled = (route: RouteInterface, userPermissionsArray: UserRoles[], reportingLevelDashboards: string[]) => {
  if (!reportingLevelDashboards) {
    return [false, false];
  }

  const isUserAllowed = userHasPermission(route.requiredUserRoles, userPermissionsArray);
  const navItemEnabled = !route.navItemPermissionId || reportingLevelDashboards.includes(route.navItemPermissionId);

  return [navItemEnabled, isUserAllowed];
};
